// src/components/Navbar.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { RxHamburgerMenu } from 'react-icons/rx';
import { FaBell } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { LOGO_URL } from '../config/assets';

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_BASE ||
  (typeof window !== 'undefined' && window.location.port === '3000'
    ? 'http://localhost:3002'
    : '');

const Navbar = ({ username, onLogout, isAdmin }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= 768 : true);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [fixedHeight, setFixedHeight] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const fixedWrapRef = useRef(null);
  const sseRef = useRef(null);
  const pollRef = useRef(null);
  const inFlightRef = useRef(false);
  useEffect(() => {
    const onResize = () => setIsDesktop(typeof window !== 'undefined' ? window.innerWidth >= 768 : true);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const measure = () => setFixedHeight(fixedWrapRef.current?.offsetHeight || 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  useEffect(() => {
    const fetchUnread = async () => {
      if (inFlightRef.current) return; // avoid overlapping polls
      inFlightRef.current = true;
      try {
        const res = await fetch(`${API_BASE}/api/notifications/unread-count`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setUnreadCount(data.count || 0);
        }
      } catch (err) {
        console.error('Failed to fetch unread count', err);
      } finally {
        inFlightRef.current = false;
      }
    };

    const startPoll = () => {
      if (pollRef.current || sseRef.current) return;
      const interval = document.hidden ? 600000 : 300000; // slower when tab hidden
      pollRef.current = setInterval(fetchUnread, interval);
    };

    const startSSE = () => {
      if (!username || sseRef.current) return;
      const es = new EventSource(`${API_BASE}/api/notifications/stream`, { withCredentials: true });
      sseRef.current = es;
      es.onopen = () => {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      };
      es.onmessage = (event) => {
        try {
          const n = JSON.parse(event.data);
          setNotifications((prev) => [n, ...prev].slice(0, 50));
          if (!n.isRead) setUnreadCount((c) => c + 1);
        } catch (err) {
          console.error('Failed to parse SSE notification', err);
        }
      };
      es.onerror = () => {
        es.close();
        sseRef.current = null;
        startPoll();
      };
    };

    fetchUnread();
    startSSE();
    startPoll();

    const handleVisibility = () => {
      if (document.hidden) {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } else {
        fetchUnread();
        startPoll();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [username]);

  const fetchNotifications = async () => {
    setNotifLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/notifications`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
      }
    } catch (err) {
      console.error('Failed to fetch notifications list', err);
    } finally {
      setNotifLoading(false);
    }
  };

  const markAllRead = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/notifications/read-all`, { method: 'PATCH', credentials: 'include' });
      if (res.ok) {
        setUnreadCount(0);
        setNotifications((prev) => prev.map(n => ({ ...n, isRead: true })));
      }
    } catch (err) {
      console.error('Failed to mark notifications read', err);
    }
  };

  const clearAll = async () => {
    setClearLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/notifications`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (err) {
      console.error('Failed to clear notifications', err);
    } finally {
      setClearLoading(false);
    }
  };

 //Format timestamps exactly as stored in the database (UTC) without shifting to the viewer's local timezone.
  const formatDbTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(undefined, { timeZone: 'UTC' });
  };

  const toggleNotifications = () => {
    const next = !showNotifications;
    setShowNotifications(next);
    if (next) fetchNotifications();
  };

  const go = (path) => {
    navigate(path);
    setIsMenuOpen(false); // ensure mobile overlay closes after navigation
  };

  const activeKey = useMemo(() => {
    if (hoveredLink) return hoveredLink;
    if (location.pathname.startsWith('/landing') || location.pathname.startsWith('/welcome')) return 'landing';
    if (location.pathname.startsWith('/home')) return 'home';
    if (location.pathname.startsWith('/owner-details')) return 'owner';
    if (location.pathname.startsWith('/cao')) return 'cao';
    if (location.pathname.startsWith('/add')) return 'add';
    if (location.pathname.startsWith('/recyclebin')) return 'recycle';
    return '';
  }, [hoveredLink, location.pathname]);

  // Styles kept as‑is (no global style changes)
  const styles = {
    fixedWrapper: { position: 'relative', top: 0, left: 0, right: 0, zIndex: 1000, backgroundColor: 'transparent' },
    spacer: { height: fixedHeight },
    navContainer: {
      padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      backgroundColor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius:'10px', maxWidth: '1440px', margin: '0 auto', width: '98%'
    },
    logoImg: {  height: windowWidth > 1000 ? 38 : 25,  width: 'auto', display: 'block', objectFit: 'contain', cursor: 'pointer', userSelect: 'none' },
    navItems: { display: 'flex', gap: '28px', padding: '10px 50px' },
    navLinkWrapper: { position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 6, paddingBottom: 4, cursor: 'pointer', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif', overflow: 'visible', whiteSpace: 'nowrap' },
    linkText: { fontSize: 18, fontWeight: 600, color: '#023e8a', WebkitTextFillColor: '#023e8a', lineHeight: 1.2, letterSpacing: '0.2px' },
    underline: { position: 'absolute', bottom: 0, left: 0, height: 2, width: '100%', backgroundColor: '#023e8a', transform: 'scaleX(0)', transformOrigin: 'left', transition: 'transform 0.25s ease' },
    underlineActive: { transform: 'scaleX(1)' },
    mobileMenuIcon: { fontSize: 22, cursor: 'pointer', color: '#0B3D2C' },
    mobileOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: isMenuOpen ? '36vh' : 0, backgroundColor: isMenuOpen ? '#023e8a' : 'transparent', display: isMenuOpen ? 'flex' : 'none', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 1100, transition: 'height 0.25s ease' },
    mobileLink: { fontSize: 18, fontWeight: 600, color: 'white', WebkitTextFillColor: 'white', margin: '12px 0', cursor: 'pointer' },
    closeIcon: { position: 'absolute', top: 16, right: 16, color: 'white', fontSize: 28, cursor: 'pointer' },
    mobileActions: { display: 'flex', alignItems: 'center', gap: 12, paddingLeft: 12, marginLeft: 8, borderLeft: '1px solid #dfe3eb' },
    mobileBellWrapper: { position: 'relative', padding: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    mobileBadge: {
      position: 'absolute',
      top: 2,
      right: 0,
      background: '#d90429',
      color: '#fff',
      borderRadius: 10,
      padding: '1px 5px',
      fontSize: 10,
      fontWeight: 700,
      minWidth: 16,
      textAlign: 'center',
      lineHeight: 1.2
    }
  };

  return (
    <>
      <div ref={fixedWrapRef} style={styles.fixedWrapper}>
        <div style={styles.navContainer}>
          {/* Logo → Landing */}
          <img src={LOGO_URL} alt="Thomas Y. Pickett" style={styles.logoImg} onClick={() => go('/landing')} />

          {isDesktop ? (
            <div style={styles.navItems}>
              {/* Landing Page */}
              <div
                style={styles.navLinkWrapper}
                onClick={() => go('/landing')}
                onMouseEnter={() => setHoveredLink('landing')}
                onMouseLeave={() => setHoveredLink(null)}
              >
                <span style={styles.linkText}>Home</span>
                <span style={{ ...styles.underline, ...(activeKey === 'landing' ? styles.underlineActive : {}) }} />
              </div>

              {/* Home */}
              <div
                style={styles.navLinkWrapper}
                onClick={() => go('/home')}
                onMouseEnter={() => setHoveredLink('home')}
                onMouseLeave={() => setHoveredLink(null)}
              >
                <span style={styles.linkText}>Documents</span>
                <span style={{ ...styles.underline, ...(activeKey === 'home' ? styles.underlineActive : {}) }} />
              </div>

              {/* Owner Detail (visible to admin & non-admin) */}
              <div
                style={styles.navLinkWrapper}
                onClick={() => go('/owner-details')}
                onMouseEnter={() => setHoveredLink('owner')}
                onMouseLeave={() => setHoveredLink(null)}
              >
                <span style={styles.linkText}>Owner Detail</span>
                <span style={{ ...styles.underline, ...(activeKey === 'owner' ? styles.underlineActive : {}) }} />
              </div>

              {/* CAO (visible to all) */}
              <div
                style={styles.navLinkWrapper}
                onClick={() => go('/cao')}
                onMouseEnter={() => setHoveredLink('cao')}
                onMouseLeave={() => setHoveredLink(null)}
              >
                <span style={styles.linkText}>CAO</span>
                <span style={{ ...styles.underline, ...(activeKey === 'cao' ? styles.underlineActive : {}) }} />
              </div>

              {/* Add New */}
              <div
                style={styles.navLinkWrapper}
                onClick={() => go('/add')}
                onMouseEnter={() => setHoveredLink('add')}
                onMouseLeave={() => setHoveredLink(null)}
              >
                <span style={styles.linkText}>Add New</span>
                <span style={{ ...styles.underline, ...(activeKey === 'add' ? styles.underlineActive : {}) }} />
              </div>

              

              {/* Recycle Bin */}
              <div
                style={styles.navLinkWrapper}
                onClick={() => go('/recyclebin')}
                onMouseEnter={() => setHoveredLink('recycle')}
                onMouseLeave={() => setHoveredLink(null)}
              >
                <span style={styles.linkText}>Recycle Bin</span>
                <span style={{ ...styles.underline, ...(activeKey === 'recycle' ? styles.underlineActive : {}) }} />
              </div>

              {/* Logout */}
              <div
                style={styles.navLinkWrapper}
                onClick={onLogout}
                onMouseEnter={() => setHoveredLink('logout')}
                onMouseLeave={() => setHoveredLink(null)}
              >
                <span style={styles.linkText}>Logout</span>
                <span style={{ ...styles.underline, ...(activeKey === 'logout' ? styles.underlineActive : {}) }} />
              </div>

              {/* Notifications */}
              <div
                style={{ ...styles.navLinkWrapper, position: 'relative' }}
                onClick={toggleNotifications}
                onMouseEnter={() => setHoveredLink('notifications')}
                onMouseLeave={() => setHoveredLink(null)}
              >
                <FaBell size={18} color="#023e8a" />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: -6,
                    right: -10,
                    background: '#d90429',
                    color: '#fff',
                    borderRadius: '12px',
                    padding: '2px 6px',
                    fontSize: 11,
                    fontWeight: 700,
                    minWidth: 20,
                    textAlign: 'center',
                  }}>
                    {unreadCount}
                  </span>
                )}
              </div>


            </div>
          ) : (
            <div style={styles.mobileActions}>
              <div onClick={() => setIsMenuOpen(v => !v)} aria-label="Open menu" role="button">
                {isMenuOpen ? <AiOutlineClose style={styles.mobileMenuIcon} /> : <RxHamburgerMenu style={styles.mobileMenuIcon} />}
              </div>
              <div
                style={styles.mobileBellWrapper}
                onClick={toggleNotifications}
                aria-label="Open notifications"
                role="button"
              >
                <FaBell size={18} color="#023e8a" />
                {unreadCount > 0 && (
                  <span style={styles.mobileBadge}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Spacer keeps layout from jumping under fixed navbar */}
      <div style={styles.spacer} />

      {/* Mobile overlay */}
      <div style={styles.mobileOverlay} aria-hidden={!isMenuOpen}>
        <AiOutlineClose style={styles.closeIcon} onClick={() => setIsMenuOpen(false)} />
        <span className="mobileLink" style={styles.mobileLink} onClick={() => go('/landing')}>Home</span>
        <span className="mobileLink" style={styles.mobileLink} onClick={() => go('/home')}>Documents</span>
        <span className="mobileLink" style={styles.mobileLink} onClick={() => go('/owner-details')}>Owner Detail</span>
        <span className="mobileLink" style={styles.mobileLink} onClick={() => go('/cao')}>CAO</span>
        <span className="mobileLink" style={styles.mobileLink} onClick={() => go('/add')}>Add New</span>
        <span className="mobileLink" style={styles.mobileLink} onClick={() => go('/recyclebin')}>Recycle Bin</span>
        <span className="mobileLink" style={styles.mobileLink} onClick={onLogout}>Logout</span>
      </div>

      {/* Notifications dropdown */}
      {showNotifications && (
        <div style={{
          position: 'fixed',
          top: (fixedWrapRef.current?.getBoundingClientRect().bottom || 60) + 8,
          right: 30,
          width: 320,
          maxHeight: 400,
          overflowY: 'auto',
          background: '#fff',
          boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
          borderRadius: 10,
          zIndex: 2000,
          padding: '12px 12px 8px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontWeight: 700, color: '#023e8a' }}>Notifications</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button
                type="button"
                onClick={markAllRead}
                style={{ border: 'none', background: 'transparent', color: '#023e8a', cursor: 'pointer', fontWeight: 600 }}
              >
                Mark all read
              </button>
              <button
                type="button"
                onClick={clearAll}
                disabled={clearLoading}
                style={{ border: 'none', background: 'transparent', color: '#d90429', cursor: 'pointer', fontWeight: 600 }}
              >
                {clearLoading ? 'Clearing...' : 'Clear'}
              </button>
              <button
                type="button"
                onClick={() => setShowNotifications(false)}
                style={{ border: 'none', background: 'transparent', color: '#023e8a', cursor: 'pointer', fontWeight: 700, fontSize: 16 }}
                aria-label="Close notifications"
              >
                ×
              </button>
            </div>
          </div>
          {notifLoading ? (
            <div style={{ padding: '12px 6px', color: '#555' }}>Loading...</div>
          ) : notifications.length === 0 ? (
            <div style={{ padding: '12px 6px', color: '#555' }}>No notifications</div>
          ) : (
            notifications.map((n) => (
              <div key={n.id} style={{
                padding: '10px 8px',
                borderRadius: 8,
                marginBottom: 6,
                background: n.isRead ? '#f8f9fa' : '#e8f2ff',
                border: n.isRead ? '1px solid #eef0f2' : '1px solid #c7ddff'
              }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#023e8a' }}>{n.title || 'Notification'}</div>
                {n.message && <div style={{ fontSize: 13, color: '#333', marginTop: 4 }}>{n.message}</div>}
                <div style={{ fontSize: 11, color: '#777', marginTop: 6 }}>
                  {formatDbTime(n.createdAt)}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </>
  );
};

export default Navbar;
